import { useState } from "react";
import usePartyStore from "../../stores/party.js";
import useSocketStore from "../../stores/socket.js";
import Player from "../../modules/player/index.js";
import { Results, log } from "@tumbodash/browserPackages";
import { gameIndex } from "@tumbodash/clientGameEngine";

interface GameResultsProps {
	viewResults: boolean;
	setViewResults: (v: boolean) => void;
}

function getStoredRating(roundId: string): "like" | "dislike" | null {
	try {
		const stored = JSON.parse(localStorage.getItem("gameRatings") ?? "{}");
		return stored[roundId] ?? null;
	} catch {
		return null;
	}
}

function storeRating(roundId: string, rating: "like" | "dislike"): void {
	try {
		const stored = JSON.parse(localStorage.getItem("gameRatings") ?? "{}");
		stored[roundId] = rating;
		localStorage.setItem("gameRatings", JSON.stringify(stored));
	} catch {
		// ignore
	}
}

function GameResults({ setViewResults }: GameResultsProps) {
	log.fx("view:party:GameResults");
	const party = usePartyStore.getState().party!;
	const socket = useSocketStore.getState().socket;
	const playerId = Player.getActiveId();
	const displayRound = party.activeRound - 2;
	const roundId = party.roundOrder[displayRound];
	const game = party.rounds[roundId];
	const gameName = Object.values(gameIndex).find(g => g.slug === game.gameSlug)?.name;

	const [rating, setRating] = useState<"like" | "dislike" | null>(() => getStoredRating(roundId));

	function handleRating(value: "like" | "dislike"): void {
		if (rating === value) return;
		setRating(value);
		storeRating(roundId, value);
		socket?.emit("player:rateGame", {
			gameSlug: game.gameSlug,
			modifierIds: game.modifierIds,
			roundId,
			rating: value,
		});
	}

	const playerStanding = playerId ? game.results[playerId]?.place ?? null : null;
	const placeString = playerStanding != null ? Results.numberToOrdinal(playerStanding) : "";

	// Sort results by position
	const sortedResults = Object.entries(game.results).sort(
		([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0)
	);

	return (
		<div style={{
			minHeight: "100dvh",
			display: "flex",
			flexDirection: "column",
			background: "var(--bg-base)",
		}}>
			{/* Header */}
			<div style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				padding: "var(--space-3) var(--space-4)",
				borderBottom: "1px solid var(--border)",
			}}>
				<div>
					<div style={{
						fontSize: "0.72rem",
						fontWeight: 700,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						color: "var(--text-muted)",
					}}>
						Round Results
					</div>
					<div style={{
						fontWeight: 700,
						fontSize: "1rem",
						color: "var(--text-primary)",
					}}>
						{gameName}
					</div>
				</div>
				<button
					onClick={() => setViewResults(false)}
					style={{
						width: 36,
						height: 36,
						borderRadius: "50%",
						border: "1px solid var(--border-strong)",
						background: "transparent",
						color: "var(--text-secondary)",
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "1.1rem",
						fontFamily: "var(--font-ui)",
					}}
				>
					✕
				</button>
			</div>

			<div style={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				gap: "var(--space-5)",
				padding: "var(--space-5) var(--space-4)",
				maxWidth: 480,
				width: "100%",
				margin: "0 auto",
			}}>

				{/* ── Player's own result — hero ── */}
				{placeString && (
					<div style={{
						background: "var(--bg-surface)",
						border: "1px solid var(--border)",
						borderRadius: "var(--radius-lg)",
						padding: "var(--space-6) var(--space-5)",
						textAlign: "center",
					}}>
						<div style={{
							fontSize: "3.5rem",
							fontWeight: 900,
							letterSpacing: "-0.04em",
							lineHeight: 1,
							color: playerStanding === 1 ? "var(--reward)" : "var(--text-primary)",
						}}>
							{placeString}
						</div>
						<div style={{
							fontSize: "0.82rem",
							color: "var(--text-muted)",
							marginTop: "var(--space-2)",
							fontWeight: 600,
							letterSpacing: "0.04em",
						}}>
							your finish
						</div>
					</div>
				)}

				{/* ── Round leaderboard ── */}
				<div>
					<div style={{
						fontSize: "0.72rem",
						fontWeight: 700,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						color: "var(--text-muted)",
						marginBottom: "var(--space-3)",
					}}>
						Round Standings
					</div>
					<div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
						{sortedResults.map(([pid, result], i) => {
							const player = party.players[pid];
							const isMe = pid === playerId;
							if (!player) return null;
							return (
								<div
									key={i}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "var(--space-3)",
										padding: "var(--space-3) var(--space-4)",
										borderRadius: "var(--radius-md)",
										background: isMe ? "var(--bg-elevated)" : "var(--bg-surface)",
										border: `1px solid ${isMe ? "var(--border-strong)" : "var(--border)"}`,
									}}
								>
									<span style={{
										width: 24,
										textAlign: "center",
										fontWeight: 800,
										fontSize: "0.95rem",
										color: result.position === 1 ? "var(--reward)" : "var(--text-muted)",
										flexShrink: 0,
									}}>
										{result.position}
									</span>
									<div style={{
										width: 10,
										height: 10,
										borderRadius: "50%",
										background: player.color,
										flexShrink: 0,
										boxShadow: `0 0 6px ${player.color}88`,
									}} />
									<span style={{
										flex: 1,
										fontWeight: 600,
										fontSize: "0.95rem",
										color: player.color,
									}}>
										{player.name}
									</span>
									{isMe && (
										<span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>you</span>
									)}
									<span style={{
										fontWeight: 700,
										color: "var(--reward)",
										background: "var(--reward-muted)",
										border: "1px solid rgba(255,230,109,0.2)",
										borderRadius: "var(--radius-pill)",
										padding: "2px 10px",
										fontSize: "0.78rem",
									}}>
										+{result.score}
									</span>
								</div>
							);
						})}
					</div>
				</div>

				{/* ── Continue ── */}
				<button
					onClick={() => setViewResults(false)}
					style={{
						width: "100%",
						padding: "var(--space-4)",
						borderRadius: "var(--radius-lg)",
						border: "none",
						background: "var(--accent)",
						color: "#fff",
						fontWeight: 700,
						fontSize: "1rem",
						fontFamily: "var(--font-ui)",
						cursor: "pointer",
						boxShadow: "0 2px 12px rgba(255,107,107,0.3)",
					}}
				>
					Next Round →
				</button>

				{/* ── Game rating ── */}
				<div style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: "var(--space-3)",
				}}>
					<div style={{
						fontSize: "0.72rem",
						fontWeight: 700,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						color: "var(--text-muted)",
					}}>
						Rate this game
					</div>
					<div style={{ display: "flex", gap: "var(--space-3)" }}>
						{(["like", "dislike"] as const).map((value) => (
							<button
								key={value}
								onClick={() => handleRating(value)}
								style={{
									width: 52,
									height: 52,
									borderRadius: "50%",
									border: `2px solid ${rating === value ? "var(--accent)" : "var(--border-strong)"}`,
									background: rating === value ? "var(--accent)" : "var(--bg-surface)",
									cursor: rating === value ? "default" : "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									transition: "border-color 0.15s, background 0.15s",
								}}
							>
								<i className={value === "like" ? "bi bi-hand-thumbs-up-fill" : "bi bi-hand-thumbs-down-fill"} style={{ fontSize: "1.3rem", color: rating === value ? "#fff" : "var(--text-secondary)" }} />
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

export default GameResults;
